import React, { useEffect, useRef, useState } from 'react';
import { X, Upload, Image as ImageIcon, FileText, Trash2 } from 'lucide-react';
import '../styles/CreateAssetModal.css';

const CreateAssetModal = ({ isOpen, onClose, onSubmit, categories, loading, error}) => {
  const [formData, setFormData] = useState({
    name: '',
    subtitle: '',
    categoryId: categories && categories.length > 0 ? categories[0].id : 1,
    metadata: {
      brand: '',
      reference: '',
      year: new Date().getFullYear(),
      condition: 'Mint'
    },
    acquiredDate: null,
    acquiredPrice: null,
    currentValue: null
  });

  const [images, setImages] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [errors, setErrors] = useState({});

  const conditions = ['Mint', 'Excellent', 'Very Good', 'Good', 'Fair', 'Poor'];

  const hasOpened = useRef(false);

  useEffect(() => {
    if (isOpen && !hasOpened.current) {
      resetForm();
      hasOpened.current = true;
    }
  
    if (!isOpen) {
      hasOpened.current = false;
    }
  }, [isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'categoryId') {
      setFormData(prev => ({ ...prev, [name]: parseInt(value) }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleMetadataChange = (e) => {
    const { name, value } = e.target;
    const processedValue = name === 'year' ? parseInt(value) : value;
    setFormData(prev => ({
      ...prev,
      metadata: { ...prev.metadata, [name]: processedValue }
    }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  // Handle multiple images upload
  const handleImagesUpload = (e) => {
    const files = Array.from(e.target.files);
    const validImages = [];
    const errors = [];

    files.forEach(file => {
      if (file.size > 5 * 1024 * 1024) {
        errors.push(`${file.name} is too large (max 5MB)`);
        return;
      }
      if (!file.type.startsWith('image/')) {
        errors.push(`${file.name} is not an image`);
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        validImages.push({
          id: Date.now() + Math.random(),
          file: file,
          preview: reader.result,
          name: file.name
        });
        
        if (validImages.length + errors.length === files.length) {
          setImages(prev => [...prev, ...validImages]);
          if (errors.length > 0) {
            alert(errors.join('\n'));
          }
        }
      };
      reader.readAsDataURL(file);
    });
  };

  // Handle multiple documents upload
  const handleDocumentsUpload = (e) => {
    const files = Array.from(e.target.files);
    const validDocs = [];
    const errors = [];

    files.forEach(file => {
      if (file.size > 25 * 1024 * 1024) {
        errors.push(`${file.name} is too large (max 25MB)`);
        return;
      }

      validDocs.push({
        id: Date.now() + Math.random(),
        file: file,
        name: file.name,
        size: (file.size / (1024 * 1024)).toFixed(2) + ' MB',
        type: file.name.split('.').pop().toUpperCase()
      });
    });

    setDocuments(prev => [...prev, ...validDocs]);
    
    if (errors.length > 0) {
      alert(errors.join('\n'));
    }
  };

  const removeImage = (id) => {
    setImages(prev => prev.filter(img => img.id !== id));
  };

  const removeDocument = (id) => {
    setDocuments(prev => prev.filter(doc => doc.id !== id));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Asset name is required';
    if (!formData.metadata.brand.trim()) newErrors.brand = 'Brand is required';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      const assetData = {
        ...formData,
        images: images.map(img => img.file),
        documents: documents.map(doc => doc.file)
      };
      
      onSubmit(assetData);
      // resetForm();
      // onClose();
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      subtitle: '',
      categoryId: categories && categories.length > 0 ? categories[0].id : 1,
      metadata: {
        brand: '',
        reference: '',
        year: new Date().getFullYear(),
        condition: 'Mint',
      },
      acquiredDate: null,
      acquiredPrice: null,
      currentValue: null
    });
    setImages([]);
    setDocuments([]);
    setErrors({});
  };
  

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-container"  style={{padding:30}}>
      {loading && (
          <div className="modal-loader-overlay">
            <div className="modal-spinner" />
            <p style={{ color: "#fff", marginTop: 8 }}>
              Creating asset & holding…
            </p>
          </div>
        )}
        {/* Header */}
        <div className="modal-header">
          <div>
            <h2 className="modal-title">Add New Asset</h2>
          </div>
          <button onClick={() => { resetForm(); onClose(); }} className="close-button">
            <X size={24} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} style={{marginTop:20}}>
          <div className="modal-body">
            {/* Basic Info */}
            <div className="form-section">
              <div className="form-fields">
                {/* Asset Name */}
                <div className="form-group">
                  <label className="form-label">Asset Name *</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="e.g., Patek Philippe Nautilus 5711"
                    className={`form-input ${errors.name ? 'input-error' : ''}`}
                  />
                  {errors.name && <p className="error-text">{errors.name}</p>}
                </div>

                {/* Subtitle */}
                <div className="form-group">
                  <label className="form-label">Subtitle</label>
                  <input
                    type="text"
                    name="subtitle"
                    value={formData.subtitle}
                    onChange={handleChange}
                    placeholder="e.g., Stainless Steel · 2021"
                    className="form-input"
                  />
                </div>

                {/* Category */}
                <div className="form-group">
                  <label className="form-label">Category *</label>
                  <select
                    name="categoryId"
                    value={formData.categoryId}
                    onChange={handleChange}
                    className="form-select"
                    style={{backgroundColor: '#f9f9f9'}}
                    
                  >
                    {categories && categories.map(cat => (
                      <option style={{backgroundColor: '#f9f9f9'}} key={cat.id} value={cat.id}>{cat.label}</option>
                    ))}
                  </select>
                </div>

                {/* Brand */}
                <div className="form-group">
                  <label className="form-label">Brand *</label>
                  <input
                    type="text"
                    name="brand"
                    value={formData.metadata.brand}
                    onChange={handleMetadataChange}
                    placeholder="e.g., Patek Philippe"
                    className={`form-input ${errors.brand ? 'input-error' : ''}`}
                  />
                  {errors.brand && <p className="error-text">{errors.brand}</p>}
                </div>

                {/* Reference */}
                <div className="form-group">
                  <label className="form-label">Reference</label>
                  <input
                    type="text"
                    name="reference"
                    value={formData.metadata.reference}
                    onChange={handleMetadataChange}
                    placeholder="e.g., 5711/1A-010"
                    className="form-input"
                  />
                </div>

                {/* Year and Condition */}
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Year</label>
                    <input
                      type="number"
                      name="year"
                      value={formData.metadata.year}
                      onChange={handleMetadataChange}
                      placeholder="2021"
                      min="1900"
                      max={new Date().getFullYear()}
                      className="form-input"
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Condition</label>
                    <select
                      name="condition"
                      value={formData.metadata.condition}
                      onChange={handleMetadataChange}
                      className="form-select"
                    >
                      {conditions.map(condition => (
                        <option key={condition} value={condition}>{condition}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            </div>

            <div className="form-section">
              <div className="section-header">
                <h3 className="section-title">Acquisition Details</h3>
              </div>

              <div className="form-row">
                {/* Acquired Date */}
                <div className="form-group">
                  <label className="form-label">Acquired Date</label>
                  <input
                    type="date"
                    name="acquiredDate"
                    value={formData.acquiredDate || ''}
                    onChange={handleChange}
                    className="form-input"
                  />
                </div>

                {/* Acquired Price */}
                <div className="form-group">
                  <label className="form-label">Acquired Price</label>
                  <input
                    type="number"
                    name="acquiredPrice"
                    value={formData.acquiredPrice || ''}
                    onChange={handleChange}
                    placeholder="e.g., 1800"
                    className="form-input"
                  />
                </div>

                {/* Current Value */}
                <div className="form-group">
                  <label className="form-label">Current Value</label>
                  <input
                    type="number"
                    name="currentValue"
                    value={formData.currentValue || ''}
                    onChange={handleChange}
                    placeholder="e.g., 4200"
                    className="form-input"
                  />
                </div>
              </div>
            </div>

            {/* Images Upload Section */}
            <div className="form-section">
              <div className="section-header">
                <h3 className="section-title">Images ({images.length})</h3>
              </div>
              
              <div className="upload-area">
                <input
                  type="file"
                  multiple
                  accept="image/*"
                  onChange={handleImagesUpload}
                  className="file-input"
                  id="images-upload"
                />
                <label htmlFor="images-upload" className="upload-label-box">
                  <ImageIcon size={32} className="upload-icon" />
                  <p className="upload-text">Click to upload images</p>
                  <p className="upload-subtext">PNG, JPG up to 5MB each</p>
                </label>
              </div>

              {images.length > 0 && (
                <div className="files-grid">
                  {images.map(image => (
                    <div key={image.id} className="file-item image-item">
                      <img src={image.preview} alt={image.name} className="image-thumbnail" />
                      <button
                        type="button"
                        onClick={() => removeImage(image.id)}
                        className="remove-file-button"
                      >
                        <Trash2 size={14} />
                      </button>
                      <p className="file-name">{image.name}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Documents Upload Section */}
            <div className="form-section">
              <div className="section-header">
                <h3 className="section-title">Documents ({documents.length})</h3>
              </div>
              
              <div className="upload-area">
                <input
                  type="file"
                  multiple
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                  onChange={handleDocumentsUpload}
                  className="file-input"
                  id="documents-upload"
                />
                <label htmlFor="documents-upload" className="upload-label-box">
                  <FileText size={32} className="upload-icon" />
                  <p className="upload-text">Click to upload documents</p>
                  <p className="upload-subtext">PDF, DOC, JPG, PNG up to 25MB each</p>
                </label>
              </div>

              {documents.length > 0 && (
                <div className="files-list">
                  {documents.map(doc => (
                    <div key={doc.id} className="file-item doc-item">
                      <div className="doc-icon">
                        <FileText size={20} />
                      </div>
                      <div className="doc-info">
                        <p className="doc-name">{doc.name}</p>
                        <p className="doc-meta">{doc.type} · {doc.size}</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeDocument(doc.id)}
                        className="remove-doc-button"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="modal-footer" style={{marginTop:20}}>
            <button
              type="button"
              onClick={() => { resetForm(); onClose(); }}
              className="button-secondary"
            >
              Cancel
            </button>
            <button type="submit" className="button-primary">
              Add Asset
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateAssetModal;